import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import { MatomoData } from '@sc/data-access-models';
import { BehaviorSubject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { TagManagerBase } from '../types';

@Injectable({
  providedIn: 'root',
})
export class MatomoService extends TagManagerBase {
  private readonly matomoConfig: MatomoData;
  private readonly matomoLoaded = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly configService: ConfigurationService,
    @Inject(DOCUMENT) private readonly doc: Document
  ) {
    super();

    this.matomoConfig = this.configService.getConfig().matomoData;

    this.initMatomo();
  }

  private afterMatomoLoaded() {
    return this.matomoLoaded.pipe(
      filter((isLoaded) => !!isLoaded),
      take(1)
    );
  }

  private getTracker() {
    const windowWith_paq: Window & { _paq: any[] } = window as any;
    windowWith_paq._paq = windowWith_paq._paq || [];
    return windowWith_paq._paq;
  }

  private getDataLayer() {
    const windowWith_mtm: Window & { _mtm: any[] } = window as any;
    windowWith_mtm._mtm = windowWith_mtm._mtm || [];
    return windowWith_mtm._mtm;
  }

  private initMatomo() {
    if (!this.usable()) {
      return;
    }

    this.matomoLoaded
      .pipe(
        filter((isLoaded) => !isLoaded),
        take(1)
      )
      .subscribe(() => {
        this.getDataLayer().push({
          'mtm.startTime': new Date().getTime(),
          event: 'mtm.Start',
        });

        const _paq = this.getTracker();
        _paq.push(['setDomains', this.matomoConfig.matomo_domains]);
        _paq.push(['enableCrossDomainLinking']);
        _paq.push(['trackPageView']);
        _paq.push(['enableLinkTracking']);
        _paq.push(['setTrackerUrl', this.matomoConfig.matomo_tracker_url]);
        _paq.push(['setSiteId', this.matomoConfig.matomo_siteId]);

        const matomoScript = this.doc.createElement('script');
        matomoScript.id = 'Matomoscript';
        matomoScript.async = true;
        matomoScript.src = this.matomoConfig.matomo_tracker_script;
        matomoScript.addEventListener('error', (err) =>
          console.error(
            'Failed To Load Matomo with config :',
            this.matomoConfig,
            err
          )
        );
        this.doc.head.insertBefore(matomoScript, this.doc.head.firstChild);

        /*
        HELP : https://developer.matomo.org/api-reference/tracking-javascript
        */
        const matomoTMScript = this.doc.createElement('script');
        matomoTMScript.id = 'MatomoTMscript';
        matomoTMScript.async = true;
        matomoTMScript.src = this.matomoConfig.matomo_tag_manager_script;
        matomoTMScript.addEventListener('load', () =>
          this.matomoLoaded.next(true)
        );
        matomoTMScript.addEventListener('error', (err) =>
          console.error(
            'Failed To Load Matomo TagManager with config :',
            this.matomoConfig,
            err
          )
        );
        this.doc.head.insertBefore(matomoTMScript, this.doc.head.firstChild);
      });
  }
  usable() {
    return this.matomoConfig;
  }

  pushTag(infos: any) {
    if (!this.usable()) {
      return;
    }

    this.afterMatomoLoaded().subscribe(() => {
      this.getDataLayer().push(infos);
      if (infos.event === 'page') {
        const _paq = this.getTracker();
        _paq.push(['setCustomUrl', infos.pageName]);
        _paq.push(['setDocumentTitle', infos.pageName]);
        _paq.push(['trackPageView']);
      }
      console.log('MatomoService pushTag', infos);
    });
  }
}
